/**
 *
 * EnvAuthenticatorLayout
 *
 */

import { stylize } from "@envestnet/envreact-component-library";
import classnames from "classnames";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import withIntl from "utils/withIntl";

import CentricLayout from "components/CentricLayout";
import DashboardMfaLayout from "components/DashboardMfaLayout";
import { mfaFlow } from "utils/enum";
import EnvAuth from "./EnvAuth";
import {
  EnvAuthenticatorStatusEnum,
  PassCodeSubmissionStatusEnum,
} from "./enums";
import messages from "./messages";
import styles, { Styles } from "./styles";
import { UserInfo } from "types/UserInfo";

interface InjectedProps extends Styles {
  /** Internal - injected from withIntl */
  formatMessage: FormatMessage;

  /** Internal - injected from WithStyles */
  innerRef?: React.RefObject<HTMLDivElement>;
}

interface DefaultProps {
  // Default props should always be required props, i.e. no ? indicating it's optional
  // The default values are specified below
}

export interface EnvAuthenticatorLayoutProps extends DefaultProps {
  /** CSS class name to be applied to top level component */
  className?: string;

  /** Inline style object to be applied to top level component */
  style?: React.CSSProperties;

  goToMfaScreen: (val: string) => void;
  loadQrCode: (obj: {
    authenticatorType: string;
    currentLayout: mfaFlow;
  }) => void;
  submitPasscode: () => void;
  loadStatus: () => void;
  reset: () => void;
  resetSubmissionStatus: () => void;
  loadingPasscodeSubmission: any;
  qrCodeImg: any;
  status: EnvAuthenticatorStatusEnum;
  secretCode: string;
  username: string;
  userInfo: UserInfo;
  isDirectLoginEnabled: boolean;
  goToSecuritySettings: () => void;
  hasUpdatedTnCAccepted: boolean;
  tncForCidpUser: string | null;
  getTnCForCidpUser: () => void;
  setUserAcceptedNewTnC: () => void;
}

const defaultProps: DefaultProps = {};

type Props = InjectedProps & EnvAuthenticatorLayoutProps;

const EnvAuthenticatorLayout = (props: Props) => {
  const {
    classes,
    style,
    className,
    formatMessage,
    innerRef,
    goToMfaScreen,
    loadQrCode,
    loadStatus,
    reset,
    resetSubmissionStatus,
    qrCodeImg,
    loadingPasscodeSubmission,
    status,
    secretCode,
    username,
    submitPasscode,
  } = props;

  const [activeStep, setActiveStep] = useState<number>(1);
  const { state } = useLocation();

  const redirectToMfa = () => {
    goToMfaScreen(state.navigationFrom);
  };

  useEffect(() => {
    if (loadingPasscodeSubmission === PassCodeSubmissionStatusEnum.DONE) {
      redirectToMfa();
    }
  }, [loadingPasscodeSubmission]);

  useEffect(() => {
    if (status === EnvAuthenticatorStatusEnum.PENDING) setActiveStep(2);
    else if (status === EnvAuthenticatorStatusEnum.VERIFIED) {
      redirectToMfa();
    }
  }, [status]);

  useEffect(() => () => reset(), []);

  return state.currentLayout === mfaFlow.MFA ? (
    <DashboardMfaLayout>
      <CentricLayout className={classes.mfaCenter}>
        <EnvAuth
          hideLogo
          secretCode={secretCode}
          resetSubmissionStatus={resetSubmissionStatus}
          username={username}
          loadQrCode={loadQrCode}
          currentLayout={state?.currentLayout}
          qrCodeImg={qrCodeImg}
          submitPasscode={submitPasscode}
          loadingPasscodeSubmission={loadingPasscodeSubmission}
          loadStatus={loadStatus}
          activeStep={activeStep}
          setActiveStep={setActiveStep}
          redirectToMfa={redirectToMfa}
          authenticatorName={formatMessage(messages.header)}
          status={status}
        />
      </CentricLayout>
    </DashboardMfaLayout>
  ) : (
    <div
      ref={innerRef}
      className={classnames(className, classes.container)}
      style={style}
    >
      <CentricLayout>
        <div>
          <EnvAuth
            hideLogo={false}
            secretCode={secretCode}
            resetSubmissionStatus={resetSubmissionStatus}
            username={username}
            loadQrCode={loadQrCode}
            currentLayout={state?.currentLayout}
            qrCodeImg={qrCodeImg}
            submitPasscode={submitPasscode}
            loadingPasscodeSubmission={loadingPasscodeSubmission}
            loadStatus={loadStatus}
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            redirectToMfa={redirectToMfa}
            authenticatorName={formatMessage(messages.header)}
            status={status}
          />
        </div>
      </CentricLayout>
    </div>
  );
};

EnvAuthenticatorLayout.defaultProps = defaultProps;

export default withIntl(
  stylize<Props, DefaultProps>(styles)(EnvAuthenticatorLayout)
);
